import { Error as ErrorIcon } from '@mui/icons-material';
import { alpha, createTheme } from '@mui/material/styles';

import { ThemeMode } from 'tools/constants/profile';

import { omit } from 'helpers/general';

import {
  blue10,
  blue7,
  blue8,
  blue9,
  gray15,
  gray18,
  green12,
  green13,
  green14,
  green15,
  porticoThemeColors,
  purple1,
  purple6,
  red10,
  red11,
  red12,
  red13,
  red14,
  red15,
  red9,
  yellow8,
} from './colors';

import type { ChipClasses } from '@mui/material/Chip';
import type { useTheme } from '@mui/material/styles';
import type { OverridesStyleRules } from '@mui/material/styles/overrides';

const desktopModeMediaQuery = '@media (min-width: 600px)';

export const getPrimaryTextColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.primaryText.light
    : porticoThemeColors.primaryText.dark;

export const getSecondaryTextColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.secondaryText.light
    : porticoThemeColors.secondaryText.dark;

export const getContrastTextColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.primaryText.dark
    : porticoThemeColors.primaryText.light;

export const getDefaultBackgroundColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.background.light
    : porticoThemeColors.background.dark;

export const getPaperBackgroundColor = (isLight: boolean) =>
  isLight ? porticoThemeColors.card.light : porticoThemeColors.card.dark;

export const getLineColor = (isLight: boolean) =>
  isLight ? porticoThemeColors.divider.light : porticoThemeColors.divider.dark;

export const getProgressColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.divider.light
    : porticoThemeColors.inputField.dark;

export const getPrimaryColor = (isLight: boolean) =>
  isLight ? porticoThemeColors.primary.light : porticoThemeColors.primary.dark;

export const getPrimaryBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.primaryBackground.light
    : porticoThemeColors.primaryBackground.dark;

export const getPrimaryForeground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.primaryForeground.light
    : porticoThemeColors.primaryForeground.dark;

export const getSecondaryColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.secondary.light
    : porticoThemeColors.secondary.dark;

export const getSecondaryBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.secondaryBackground.light
    : porticoThemeColors.secondaryBackground.dark;

export const getSecondaryForeground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.secondaryForeground.light
    : porticoThemeColors.secondaryForeground.dark;

export const getErrorColor = (isLight: boolean) =>
  isLight ? porticoThemeColors.error.light : porticoThemeColors.error.dark;

export const getErrorBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.errorBackground.light
    : porticoThemeColors.errorBackground.dark;

export const getErrorForeground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.errorForeground.light
    : porticoThemeColors.errorForeground.dark;

export const getWarningColor = (isLight: boolean) =>
  isLight ? porticoThemeColors.warning.light : porticoThemeColors.warning.dark;

export const getWarningBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.warningBackground.light
    : porticoThemeColors.warningBackground.dark;

export const getWarningForeground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.warningForeground.light
    : porticoThemeColors.warningForeground.dark;

export const getAlertColors = (isLight: boolean) =>
  isLight ? porticoThemeColors.alert.light : porticoThemeColors.alert.dark;

export const getNeutralColors = (isLight: boolean) =>
  isLight ? porticoThemeColors.neutral.light : porticoThemeColors.neutral.dark;

export const getMonochromeColor = (isLight: boolean) =>
  isLight ? porticoThemeColors.white : porticoThemeColors.black;

export const getButtonOutlineColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.buttonOutline.light
    : porticoThemeColors.buttonOutline.dark;

export const getButtonContainedPrimaryHoverColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.buttonContained.primary.hover.light
    : porticoThemeColors.buttonContained.primary.hover.dark;

export const getButtonContainedErrorHoverColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.buttonContained.error.hover.light
    : porticoThemeColors.buttonContained.error.hover.dark;

export const getToggleButtonSelectedBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.toggleButton.selected.backgroundColor.light
    : porticoThemeColors.toggleButton.selected.backgroundColor.dark;

export const getOverlayHoverColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.overlay.hover.light
    : porticoThemeColors.overlay.hover.dark;

export const getOverlaySelectedColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.overlay.selected.light
    : porticoThemeColors.overlay.selected.dark;

export const getInputFieldColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.inputField.light
    : porticoThemeColors.inputField.dark;

export const getIconColor = (isLight: boolean) =>
  isLight ? porticoThemeColors.icon.light : porticoThemeColors.icon.dark;

export const getInfoColors = (isLight: boolean) => ({
  background: isLight
    ? porticoThemeColors.info.background.light
    : porticoThemeColors.info.background.dark,
  foreground: isLight
    ? porticoThemeColors.info.foreground.light
    : porticoThemeColors.info.foreground.dark,
});

export const getLinkEmphasisColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.linkEmphasis.light
    : porticoThemeColors.linkEmphasis.dark;

export const getLoginBackgroundColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.login.backgroundColor.light
    : porticoThemeColors.login.backgroundColor.dark;

export const getNudgeChannelBackgroundColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeChannel.backgroundColor.light
    : porticoThemeColors.nudgeChannel.backgroundColor.dark;

export const getNudgeChannelBorderColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeChannel.borderColor.light
    : porticoThemeColors.nudgeChannel.borderColor.dark;

export const getNudgeChannelIconColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeChannel.iconColor.light
    : porticoThemeColors.nudgeChannel.iconColor.dark;

export const getNudgeFrequencyBackgroundColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeFrequency.backgroundColor.light
    : porticoThemeColors.nudgeFrequency.backgroundColor.dark;

export const getNudgeFrequencyBorderColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeFrequency.borderColor.light
    : porticoThemeColors.nudgeFrequency.borderColor.dark;

export const getNudgeFrequencyIconColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeFrequency.iconColor.light
    : porticoThemeColors.nudgeFrequency.iconColor.dark;

export const getNudgeAudiencePreviewChipBackgroundColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeAudiencePreview.chip.backgroundColor.light
    : porticoThemeColors.nudgeAudiencePreview.chip.backgroundColor.dark;

export const getNudgeAudiencePreviewChipColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeAudiencePreview.chip.color.light
    : porticoThemeColors.nudgeAudiencePreview.chip.color.dark;

export const getNudgeFilterBackgroundColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeFilter.backgroundColor.light
    : porticoThemeColors.nudgeFilter.backgroundColor.dark;

export const getNudgeFilterBorderColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeFilter.borderColor.light
    : porticoThemeColors.nudgeFilter.borderColor.dark;

export const getNudgeFilterIconColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeFilter.iconColor.light
    : porticoThemeColors.nudgeFilter.iconColor.dark;

export const getLearnosityBackgroundErrorColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.learnosity.error.background.light
    : porticoThemeColors.learnosity.error.background.dark;

export const getLearnosityBackgroundSuccessColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.learnosity.success.background.light
    : porticoThemeColors.learnosity.success.background.dark;

export const getLearnosityBackgroundWarningColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.learnosity.warning.background.light
    : porticoThemeColors.learnosity.warning.background.dark;

export const getLearnosityForegroundErrorColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.learnosity.error.foreground.light
    : porticoThemeColors.learnosity.error.foreground.dark;

export const getLearnosityForegroundSuccessColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.learnosity.success.foreground.light
    : porticoThemeColors.learnosity.success.foreground.dark;

export const getLearnosityForegroundWarningColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.learnosity.warning.foreground.light
    : porticoThemeColors.learnosity.warning.foreground.dark;

export const getInputColors = (isLight: boolean) =>
  isLight ? porticoThemeColors.input.light : porticoThemeColors.input.dark;

export const getTabColors = (isLight: boolean) =>
  isLight ? porticoThemeColors.tab.light : porticoThemeColors.tab.dark;

export const getFillColors = (isLight: boolean) =>
  isLight ? porticoThemeColors.fill.light : porticoThemeColors.fill.dark;

export const getNudgeDashboardColors = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.nudgeDashboard.light
    : porticoThemeColors.nudgeDashboard.dark;

export const getPaymentCoralBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.payment.coral.background.light
    : porticoThemeColors.payment.coral.background.dark;

export const getPaymentCoralForeground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.payment.coral.foreground.light
    : porticoThemeColors.payment.coral.foreground.dark;

export const getFooterBoxShadow = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.boxShadow.light
    : porticoThemeColors.boxShadow.dark;

export const getPlanCardButtonBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.planCardButton.background.light
    : porticoThemeColors.planCardButton.background.dark;

export const getPlanCardButtonHoverBackground = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.planCardButton.hoverBackground.light
    : porticoThemeColors.planCardButton.hoverBackground.dark;

export const getSeparatorColor = (isLight: boolean) =>
  isLight
    ? porticoThemeColors.separator.light
    : porticoThemeColors.separator.dark;

/**
 * The colors come from the porticoThemeColors object, which is used
 * here to set the global colors for the MUI theme. The porticoThemeColors
 * can also be used directly by importing it where needed.
 */
export const getTheme = (mode: ThemeMode.Dark | ThemeMode.Light) => {
  const isLight = mode === ThemeMode.Light;

  const lineColor = getLineColor(isLight);
  const lineWidth = '1px';

  const progressColor = getProgressColor(isLight);
  const inputFieldColor = getInputFieldColor(isLight);
  const iconColor = getIconColor(isLight);
  const infoColors = getInfoColors(isLight);
  const linkEmphasisColor = getLinkEmphasisColor(isLight);
  const boxShadowColor = getFooterBoxShadow(isLight);
  const monochromeColor = getMonochromeColor(isLight);
  const loginBackgroundColor = getLoginBackgroundColor(isLight);
  const inputColors = getInputColors(isLight);

  const textColors = {
    contrast: getContrastTextColor(isLight),
    primary: getPrimaryTextColor(isLight),
    primaryDisabled: alpha(getPrimaryTextColor(isLight), 0.5),
    secondary: getSecondaryTextColor(isLight),
    secondaryDisabled: alpha(getSecondaryTextColor(isLight), 0.5),
  };

  const backgroundColors = {
    default: getDefaultBackgroundColor(isLight),
    paper: getPaperBackgroundColor(isLight),
  };

  const overlayColors = {
    hover: getOverlayHoverColor(isLight),
    selected: getOverlaySelectedColor(isLight),
  };

  const primaryColors = {
    background: getPrimaryBackground(isLight),
    disabled: alpha(getPrimaryColor(isLight), 0.5),
    foreground: getPrimaryForeground(isLight),
    hover: alpha(getPrimaryColor(isLight), 0.2),
    main: getPrimaryColor(isLight),
    selected: alpha(getPrimaryColor(isLight), 0.3),
  };

  const secondaryColors = {
    background: getSecondaryBackground(isLight),
    disabled: alpha(getSecondaryColor(isLight), 0.5),
    foreground: getSecondaryForeground(isLight),
    hover: alpha(getSecondaryColor(isLight), 0.2),
    main: getSecondaryColor(isLight),
  };

  const errorColors = {
    background: getErrorBackground(isLight),
    disabled: alpha(getErrorColor(isLight), 0.5),
    foreground: getErrorForeground(isLight),
    hover: alpha(getErrorColor(isLight), 0.2),
    main: getErrorColor(isLight),
  };

  const warningColors = {
    background: getWarningBackground(isLight),
    disabled: alpha(getWarningColor(isLight), 0.5),
    foreground: getWarningForeground(isLight),
    hover: alpha(getWarningColor(isLight), 0.2),
    main: getWarningColor(isLight),
  };

  const alertColors = {
    ...getAlertColors(isLight),
    contrastText: textColors.contrast,
  };

  const neutralColors = getNeutralColors(isLight);

  const buttonOutlineColors = {
    disabled: alpha(getButtonOutlineColor(isLight), 0.5),
    main: getButtonOutlineColor(isLight),
  };
  const buttonContainedHoverColors = {
    error: getButtonContainedErrorHoverColor(isLight),
    primary: getButtonContainedPrimaryHoverColor(isLight),
  };
  const buttonColors = {
    contained: {
      error: {
        disabled: {
          backgroundColor: errorColors.disabled,
          color: textColors.contrast,
        },
        hover: {
          backgroundColor: buttonContainedHoverColors.error,
        },
      },
      primary: {
        disabled: {
          backgroundColor: primaryColors.disabled,
          color: textColors.contrast,
        },
        hover: {
          backgroundColor: buttonContainedHoverColors.primary,
        },
      },
    },
    outlined: {
      default: {
        disabled: buttonOutlineColors.disabled,
        hover: overlayColors.hover,
        main: buttonOutlineColors.main,
      },
      error: {
        disabled: errorColors.disabled,
        hover: errorColors.hover,
        main: errorColors.main,
      },
      success: {
        disabled: primaryColors.disabled,
        hover: primaryColors.hover,
        main: primaryColors.main,
      },
    },
    text: {
      error: {
        disabled: {
          color: errorColors.disabled,
        },
        hover: {
          backgroundColor: errorColors.hover,
        },
        main: {
          color: errorColors.main,
        },
      },
      primary: {
        disabled: {
          color: primaryColors.disabled,
        },
        hover: {
          backgroundColor: primaryColors.hover,
        },
        main: {
          color: primaryColors.main,
        },
      },
      secondary: {
        disabled: {
          color: secondaryColors.disabled,
        },
        hover: {
          backgroundColor: secondaryColors.hover,
        },
        main: {
          color: secondaryColors.main,
        },
      },
    },
  };
  const buttonOutlinedStyle = {
    '&.Mui-disabled': {
      border: `1px solid ${buttonColors.outlined.default.disabled}`,
      color: textColors.primaryDisabled,
    },
    '&:focus-visible, &:hover': {
      backgroundColor: buttonColors.outlined.default.hover,
      border: `1px solid ${buttonColors.outlined.default.main}`,
    },
    backgroundColor: 'transparent',
    border: `1px solid ${buttonColors.outlined.default.main}`,
    color: textColors.primary,
  };

  const toggleButtonColors = {
    backgroundColor: 'transparent',
    borderColor: buttonOutlineColors.main,
    color: textColors.primary,
    hover: {
      backgroundColor: overlayColors.hover,
    },
    selected: {
      backgroundColor: getToggleButtonSelectedBackground(isLight),
      borderColor: primaryColors.main,
      color: primaryColors.main,
    },
  };

  const switchColors = {
    checked: {
      thumb: {
        color: primaryColors.main,
      },
      track: {
        backgroundColor: primaryColors.selected,
        opacity: 1,
      },
    },
    common: {
      thumb: {
        boxShadow:
          '0px 0px 2px rgba(0, 0, 0, 0.15), 0px 2px 2px rgba(0, 0, 0, 0.25)',
      },
    },
    unchecked: {
      thumb: {
        color: porticoThemeColors.white,
      },
      track: {
        backgroundColor: inputFieldColor,
        opacity: 1,
      },
    },
  };

  const chipColors = {
    default: {
      backgroundColor: lineColor,
      color: textColors.primary,
    },
    error: {
      backgroundColor: errorColors.background,
      color: errorColors.foreground,
    },
    primary: {
      backgroundColor: primaryColors.background,
      color: primaryColors.foreground,
    },
    secondary: {
      backgroundColor: secondaryColors.background,
      color: secondaryColors.foreground,
    },
    warning: {
      backgroundColor: warningColors.background,
      color: warningColors.foreground,
    },
  };

  const outlinedInputColors = {
    input: {
      color: textColors.primary,
      disabled: {
        color: textColors.primaryDisabled,
      },
      placeholder: {
        color: textColors.secondary,
      },
    },
    root: {
      backgroundColor: inputColors.background,
      borderColor: inputColors.border,
      color: textColors.primary,
      disabled: {
        borderColor: buttonOutlineColors.disabled,
        color: textColors.primaryDisabled,
        iconColor: textColors.primaryDisabled,
      },
      error: {
        borderColor: errorColors.main,
        caretColor: textColors.primary,
      },
      focused: {
        borderColor: primaryColors.main,
        caretColor: textColors.primary,
      },
      hover: {
        borderColor: primaryColors.main,
      },
      iconColor,
    },
  };

  const nudgeChannelColors = {
    backgroundColor: getNudgeChannelBackgroundColor(isLight),
    borderColor: getNudgeChannelBorderColor(isLight),
    iconColor: getNudgeChannelIconColor(isLight),
  };

  const nudgeFrequencyColors = {
    backgroundColor: getNudgeFrequencyBackgroundColor(isLight),
    borderColor: getNudgeFrequencyBorderColor(isLight),
    iconColor: getNudgeFrequencyIconColor(isLight),
  };

  const nudgeAudiencePreviewColors = {
    chip: {
      backgroundColor: getNudgeAudiencePreviewChipBackgroundColor(isLight),
      color: getNudgeAudiencePreviewChipColor(isLight),
    },
  };

  const nudgeFilterColors = {
    backgroundColor: getNudgeFilterBackgroundColor(isLight),
    borderColor: getNudgeFilterBorderColor(isLight),
    iconColor: getNudgeFilterIconColor(isLight),
  };

  const nudgeDashboardColors = getNudgeDashboardColors(isLight);

  const learnosityColors = {
    error: {
      background: getLearnosityBackgroundErrorColor(isLight),
      foreground: getLearnosityForegroundErrorColor(isLight),
    },
    success: {
      background: getLearnosityBackgroundSuccessColor(isLight),
      foreground: getLearnosityForegroundSuccessColor(isLight),
    },
    warning: {
      background: getLearnosityBackgroundWarningColor(isLight),
      foreground: getLearnosityForegroundWarningColor(isLight),
    },
  };

  const cardKnoxInputColors = {
    background: inputColors.background,
    border: inputColors.border,
    color: textColors.primary,
  };

  const tabColors = getTabColors(isLight);
  const fillColors = getFillColors(isLight);

  const planCardColors = {
    button: {
      backgroundColor: getPlanCardButtonBackground(isLight),
      hoverBackgroundColor: getPlanCardButtonHoverBackground(isLight),
    },
  };

  const cardShadow = isLight
    ? '0px 2px 8px 0px rgba(37, 37, 41, 0.1)'
    : '0px 2px 8px #0E0E0F';
  const headerShadow = isLight
    ? '0px 0px 8px rgba(78, 88, 107, 0.12), 0px 0px 4px rgba(78, 88, 107, 0.1)'
    : '0px 0px 8px rgba(0, 0, 0, 0.9), 0px 0px 4px rgba(0, 0, 0, 0.9)';
  const drawerShadow = isLight
    ? '0px 0px 8px rgba(78, 88, 107, 0.12), 0px 0px 4px rgba(78, 88, 107, 0.1)'
    : '0px 0px 8px rgba(0, 0, 0, 0.9), 0px 0px 4px #000000';

  const cardHoverStyles = {
    boxShadow: isLight
      ? '0px 5px 10px rgba(78, 88, 107, 0.15), 0px 2px 6px rgba(78, 88, 107, 0.15)'
      : '0px 6px 12px #0F0F0F, 0px 2px 8px #0F0F0F',
    zIndex: 1,
  };

  let theme = createTheme({
    palette: {
      alert: {
        contrastText: alertColors.contrastText,
        main: alertColors.main,
      },
      background: {
        default: backgroundColors.default,
        paper: backgroundColors.paper,
      },
      boxShadow: boxShadowColor,
      brand: {
        blue: porticoThemeColors.brand.blue,
      },
      buttonOutline: {
        color: buttonColors.outlined.default.main,
        hover: {
          backgroundColor: buttonColors.outlined.default.hover,
        },
      },
      cardKnox: {
        input: cardKnoxInputColors,
      },
      completed: {
        contrastText: `${green13} !important`,
        main: `${green12} !important`,
      },
      current: {
        contrastText: `${green15} !important`,
        main: `${green14} !important`,
      },
      declined: {
        contrastText: `${red10} !important`,
        main: `${red9} !important`,
      },
      divider: lineColor,
      due: {
        contrastText: `${red15} !important`,
        main: `${red14} !important`,
      },
      error: {
        contrastText: textColors.contrast,
        light: errorColors.hover,
        main: errorColors.main,
      },
      failed: {
        contrastText: `${red13} !important`,
        main: `${red12} !important`,
      },
      fgBg: {
        error: {
          background: errorColors.background,
          foreground: errorColors.foreground,
        },
        info: {
          background: infoColors.background,
          foreground: infoColors.foreground,
        },
        neutral: {
          background: neutralColors.background,
          foreground: neutralColors.foreground,
        },
        primary: {
          background: primaryColors.background,
          foreground: primaryColors.foreground,
        },
        secondary: {
          background: secondaryColors.background,
          foreground: secondaryColors.foreground,
        },
        warning: {
          background: warningColors.background,
          foreground: warningColors.foreground,
        },
      },
      fill: {
        error: {
          backgroundColor: fillColors.error.backgroundColor,
          color: fillColors.error.color,
        },
        neutral: {
          backgroundColor: fillColors.neutral.backgroundColor,
          color: fillColors.neutral.color,
        },
        primary: {
          backgroundColor: fillColors.primary.backgroundColor,
          color: fillColors.primary.color,
        },
        secondary: {
          backgroundColor: fillColors.secondary.backgroundColor,
          color: fillColors.secondary.color,
        },
        warning: {
          backgroundColor: fillColors.warning.backgroundColor,
          color: fillColors.warning.color,
        },
      },
      iconColor,
      learnosity: learnosityColors,
      lineColor,
      linkEmphasis: linkEmphasisColor,
      loa: {
        contrastText: `${blue10} !important`,
        main: `${blue9} !important`,
      },
      login: {
        backgroundColor: loginBackgroundColor,
      },
      mode,
      monochrome: monochromeColor,
      nudge: {
        audiencePreview: {
          chip: {
            backgroundColor: nudgeAudiencePreviewColors.chip.backgroundColor,
            color: nudgeAudiencePreviewColors.chip.color,
          },
        },
        channel: {
          backgroundColor: nudgeChannelColors.backgroundColor,
          borderColor: nudgeChannelColors.borderColor,
          iconColor: nudgeChannelColors.iconColor,
        },
        dashboard: {
          categoryRate: nudgeDashboardColors.categoryRate,
          channelDistribution: nudgeDashboardColors.channelDistribution,
          channelStats: nudgeDashboardColors.channelStats,
          charts: nudgeDashboardColors.charts,
          dateRange1: nudgeDashboardColors.dateRange1,
          dateRange2: nudgeDashboardColors.dateRange2,
          filters: nudgeDashboardColors.filters,
          performanceComparison: nudgeDashboardColors.performanceComparison,
          tables: nudgeDashboardColors.tables,
          weekOverWeek: nudgeDashboardColors.weekOverWeek,
        },
        filter: {
          backgroundColor: nudgeFilterColors.backgroundColor,
          borderColor: nudgeFilterColors.borderColor,
          iconColor: nudgeFilterColors.iconColor,
        },
        frequency: {
          backgroundColor: nudgeFrequencyColors.backgroundColor,
          borderColor: nudgeFrequencyColors.borderColor,
          iconColor: nudgeFrequencyColors.iconColor,
        },
      },
      outstanding: {
        contrastText: `${red10} !important`,
        main: `${red9} !important`,
      },
      overdue: {
        contrastText: `${red10} !important`,
        main: `${red9} !important`,
      },
      overlay: {
        hover: overlayColors.hover,
        selected: overlayColors.selected,
      },
      paid: {
        contrastText: `${green15} !important`,
        main: `${green14} !important`,
      },
      partial: {
        contrastText: `${red11} !important`,
        main: `${yellow8} !important`,
      },
      pastdue: {
        contrastText: `${red11} !important`,
        main: `${yellow8} !important`,
      },
      planCard: {
        button: {
          backgroundColor: planCardColors.button.backgroundColor,
          hoverBackgroundColor: planCardColors.button.hoverBackgroundColor,
        },
      },
      primary: {
        contrastText: textColors.contrast,
        dark: primaryColors.selected,
        light: primaryColors.hover,
        main: primaryColors.main,
      },
      refunded: {
        contrastText: `${blue8} !important`,
        main: `${blue7} !important`,
      },
      secondary: {
        light: secondaryColors.hover,
        main: secondaryColors.main,
      },
      separator: getSeparatorColor(isLight),
      surface: {
        card: {
          onHover: isLight
            ? porticoThemeColors.surface.card.onHover.light
            : porticoThemeColors.surface.card.onHover.dark,
        },
        chart: {
          backgroundColor: isLight
            ? porticoThemeColors.surface.chart.backgroundColor.light
            : porticoThemeColors.surface.chart.backgroundColor.dark,
          primary: isLight
            ? porticoThemeColors.surface.chart.primary.light
            : porticoThemeColors.surface.chart.primary.dark,
        },
        chip: {
          selectedBg: isLight
            ? porticoThemeColors.surface.chip.selectedBg.light
            : porticoThemeColors.surface.chip.selectedBg.dark,
          selectedColor: isLight
            ? porticoThemeColors.surface.chip.selectedColor.light
            : porticoThemeColors.surface.chip.selectedColor.dark,
        },
      },
      tableHeader: {
        accentBackground: isLight
          ? porticoThemeColors.tableHeader.accentBackground.light
          : porticoThemeColors.tableHeader.accentBackground.dark,
        infoBackground: isLight
          ? porticoThemeColors.tableHeader.infoBackground.light
          : porticoThemeColors.tableHeader.infoBackground.dark,
      },
      text: {
        primary: textColors.primary,
        secondary: textColors.secondary,
      },
      tip: {
        main: porticoThemeColors.tip.main,
      },
      toggle: {
        backgroundColor: toggleButtonColors.backgroundColor,
        borderColor: toggleButtonColors.borderColor,
        color: toggleButtonColors.color,
        hover: {
          backgroundColor: toggleButtonColors.hover.backgroundColor,
        },
        selected: {
          backgroundColor: toggleButtonColors.selected.backgroundColor,
          borderColor: toggleButtonColors.selected.borderColor,
          color: toggleButtonColors.selected.color,
        },
      },
      upcoming: {
        contrastText: `${gray18} !important`,
        main: `${gray15} !important`,
      },
      void: {
        contrastText: `${purple1} !important`,
        main: `${purple6} !important`,
      },
    },
    typography: {
      body1: {
        color: textColors.primary,
        fontSize: '16px',
        lineHeight: '24px',
      },
      body2: {
        color: textColors.primary,
        fontSize: '14px',
        lineHeight: '20px',
      },
      button: {
        color: textColors.primary,
        fontSize: '15px',
        fontWeight: 700,
        letterSpacing: '0.015em',
        lineHeight: '24px',
        textTransform: 'unset',
      },
      caption: {
        color: textColors.secondary,
        display: 'block',
        fontSize: '12px',
        lineHeight: '20px',
      },
      fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
      fontSize: 14,
      h1: {
        color: textColors.primary,
        fontSize: '28px',
        fontWeight: 600,
        lineHeight: '40px',
      },
      h2: {
        color: textColors.primary,
        fontSize: '24px',
        fontWeight: 'bold',
        letterSpacing: '0.005em',
        lineHeight: '32px',
      },
      h3: {
        color: textColors.primary,
        fontSize: '22px',
        fontWeight: 'bold',
        letterSpacing: '0.005em',
        lineHeight: '32px',
      },
      h4: {
        color: textColors.primary,
        fontSize: '18px',
        fontWeight: 'bold',
        letterSpacing: '0.005em',
        lineHeight: '28px',
      },
      h5: {
        color: textColors.primary,
        fontSize: '16px',
        fontWeight: 'bold',
        letterSpacing: '0.005em',
        lineHeight: '24px',
      },
      h6: {
        color: textColors.primary,
        fontSize: '14px',
        fontWeight: 600,
        letterSpacing: '0.005em',
        lineHeight: '20px',
      },
      overline: {
        color: textColors.secondary,
        fontSize: '12px',
        fontWeight: 'bold',
        letterSpacing: '0.08em',
        lineHeight: '16px',
      },
      subtitle1: {
        color: textColors.secondary,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
      },
      subtitle2: {
        color: textColors.secondary,
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: '20px',
      },
    },
  });

  theme = {
    ...theme,
    components: {
      // accordions
      MuiAccordion: {
        defaultProps: {
          disableGutters: true,
        },
        styleOverrides: {
          root: {
            '&:before': {
              display: 'none',
            },
            borderBottomLeftRadius: '6px',
            borderBottomRightRadius: '6px',
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
            overflow: 'hidden',
          },
          rounded: {
            '&.MuiPaper-elevation1': {
              boxShadow: cardShadow,
            },
            '&:first-of-type': {
              borderTopLeftRadius: '6px',
              borderTopRightRadius: '6px',
            },
            '&:last-child': {
              borderBottomLeftRadius: '6px',
              borderBottomRightRadius: '6px',
            },
            borderBottomLeftRadius: '6px',
            borderBottomRightRadius: '6px',
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
          },
        },
        variants: [
          {
            props: { elevation: 0 },
            style: {
              '& .MuiAccordionSummary-root': {
                display: 'inline-flex',
                minHeight: 0,
                padding: 0,
              },
              backgroundColor: 'unset',
              lineHeight: 'normal',
            },
          },
        ],
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            display: 'block',
            padding: 0,
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          content: {
            margin: 0,
          },
          root: {
            minHeight: '64px',
            padding: '0 32px',
          },
        },
      },

      // alerts
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            error: <ErrorIcon fontSize="inherit" />,
            warning: <ErrorIcon fontSize="inherit" />,
          },
        },
        styleOverrides: {
          action: {
            alignItems: 'center',
            marginBottom: 0,
            marginTop: 0,
            paddingBottom: 0,
            paddingTop: 0,
          },
          icon: {
            fontSize: '24px',
            marginBottom: '-2px',
            marginTop: '-2px',
          },
          root: {
            borderRadius: '8px',
            boxShadow: 'none',
            color: textColors.primary,
          },
          standardError: {
            '& .MuiAlert-icon': {
              color: errorColors.foreground,
            },
            backgroundColor: errorColors.background,
          },
          standardInfo: {
            '& .MuiAlert-icon': {
              color: primaryColors.foreground,
            },
            backgroundColor: primaryColors.background,
          },
          standardWarning: {
            '& .MuiAlert-icon': {
              color: warningColors.foreground,
            },
            backgroundColor: warningColors.background,
          },
        },
      },

      // app bar
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: headerShadow,
          },
        },
      },

      // autocomplete
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              minHeight: '40px',
              padding: '5px 16px',
            },
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              padding: '0',
            },

            // with adornments
            '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
              paddingRight: '36px',
            },

            // small autocomplete
            '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
              minHeight: '32px',
              padding: '1px 16px',
            },
          },
        },
      },

      // buttons
      MuiButton: {
        defaultProps: {
          disableFocusRipple: true,
          disableTouchRipple: true,
          size: 'large',
          variant: 'contained',
        },
        styleOverrides: {
          contained: {
            '&.Mui-focusVisible': {
              boxShadow: 'none',
            },
            '&:active': {
              boxShadow: 'none',
            },
            '&:hover': {
              '@media (hover: none)': {
                boxShadow: 'none',
              },
              boxShadow: 'none',
            },
            boxShadow: 'none',
          },
          containedError: {
            '&.Mui-disabled': {
              backgroundColor:
                buttonColors.contained.error.disabled.backgroundColor,
              color: textColors.contrast,
            },
            '&:focus-visible': {
              backgroundColor:
                buttonColors.contained.error.hover.backgroundColor,
            },
            '&:hover': {
              backgroundColor:
                buttonColors.contained.error.hover.backgroundColor,
            },
          },
          containedPrimary: {
            '&.Mui-disabled': {
              backgroundColor:
                buttonColors.contained.primary.disabled.backgroundColor,
              color: textColors.contrast,
            },
            '&:focus-visible': {
              backgroundColor:
                buttonColors.contained.primary.hover.backgroundColor,
            },
            '&:hover': {
              backgroundColor:
                buttonColors.contained.primary.hover.backgroundColor,
            },
          },
          endIcon: {
            '&.MuiButton-iconSizeSmall': {
              '&> *:first-of-type': {
                fontSize: '16px',
              },
            },
            marginLeft: '4px',
          },
          outlined: buttonOutlinedStyle,
          outlinedError: {
            '&.Mui-disabled': {
              border: `1px solid ${buttonColors.outlined.error.disabled}`,
              color: buttonColors.outlined.error.disabled,
            },
            '&:focus-visible': {
              backgroundColor: buttonColors.outlined.error.hover,
              border: `1px solid ${buttonColors.outlined.error.main}`,
            },
            '&:hover': {
              backgroundColor: buttonColors.outlined.error.hover,
              border: `1px solid ${buttonColors.outlined.error.main}`,
            },
            backgroundColor: 'transparent',
            border: `1px solid ${buttonColors.outlined.error.main}`,
            color: buttonColors.outlined.error.main,
          },
          outlinedSuccess: {
            '&.Mui-disabled': {
              border: `1px solid ${buttonColors.outlined.success.disabled}`,
              color: buttonColors.outlined.success.disabled,
            },
            '&:focus-visible': {
              backgroundColor: buttonColors.outlined.success.hover,
              border: `1px solid ${buttonColors.outlined.success.main}`,
            },
            '&:hover': {
              backgroundColor: buttonColors.outlined.success.hover,
              border: `1px solid ${buttonColors.outlined.success.main}`,
            },
            backgroundColor: 'transparent',
            border: `1px solid ${buttonColors.outlined.success.main}`,
            color: buttonColors.outlined.success.main,
          },
          root: {
            boxShadow: 'none',
            boxSizing: 'border-box',
            fontWeight: 600,
            minHeight: '32px',
            padding: '0px 20px',
          },
          sizeLarge: {
            borderRadius: '10px',
            minHeight: '40px',
            padding: '0 24px',
          },
          sizeMedium: {
            borderRadius: '8px',
            minHeight: '32px',
            padding: '0px 16px',
          },
          sizeSmall: {
            ...omit(theme.typography.caption, ['color', 'display']),
            borderRadius: '8px',
            fontWeight: 'bold',
            minHeight: '24px',
            padding: '0px 12px',
          },
          startIcon: {
            '&.MuiButton-iconSizeSmall': {
              '&> *:first-of-type': {
                fontSize: '16px',
              },
              marginRight: '4px',
            },
            marginRight: '4px',
          },
          textError: {
            '&.Mui-disabled': {
              color: buttonColors.text.error.disabled.color,
            },
            '&:focus-visible': {
              backgroundColor: buttonColors.text.error.hover.backgroundColor,
            },
            '&:hover': {
              backgroundColor: buttonColors.text.error.hover.backgroundColor,
            },
            color: buttonColors.text.error.main.color,
          },
          textPrimary: {
            '&.Mui-disabled': {
              color: buttonColors.text.primary.disabled.color,
            },
            '&:focus-visible': {
              backgroundColor: buttonColors.text.primary.hover.backgroundColor,
            },
            '&:hover': {
              backgroundColor: buttonColors.text.primary.hover.backgroundColor,
            },
            color: buttonColors.text.primary.main.color,
          },
          textSecondary: {
            '&.Mui-disabled': {
              color: buttonColors.text.secondary.disabled.color,
            },
            '&:focus-visible': {
              backgroundColor:
                buttonColors.text.secondary.hover.backgroundColor,
            },
            '&:hover': {
              backgroundColor:
                buttonColors.text.secondary.hover.backgroundColor,
            },
            color: buttonColors.text.secondary.main.color,
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          contained: {
            borderRadius: '20px',
          },
        },
      },

      // checkbox
      MuiCheckbox: {
        defaultProps: {
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              '&.Mui-disabled': {
                // checked and disabled
                color: primaryColors.disabled,
              },
            },
            '&.Mui-disabled': {
              color: buttonColors.outlined.default.disabled,
            },
            '&.Mui-focusVisible': {
              // checked and focused
              '&.Mui-checked': {
                backgroundColor: primaryColors.hover,
              },
              // unchecked and focused
              backgroundColor: buttonColors.outlined.default.hover,
            },
            '&:hover': {
              // checked and hovered
              '&.Mui-checked': {
                backgroundColor: primaryColors.hover,
              },
              // unchecked and hovered
              backgroundColor: buttonColors.outlined.default.hover,
            },
            color: buttonColors.outlined.default.main,
            padding: '8px',
          },
        },
      },

      // chip
      MuiChip: {
        styleOverrides: {
          colorError: {
            '&:hover': {
              backgroundColor: chipColors.error.backgroundColor,
            },
            backgroundColor: chipColors.error.backgroundColor,
            color: chipColors.error.color,
          },
          colorPrimary: {
            '&:hover': {
              backgroundColor: chipColors.primary.backgroundColor,
            },
            backgroundColor: chipColors.primary.backgroundColor,
            color: chipColors.primary.color,
          },
          colorSecondary: {
            '&:hover': {
              backgroundColor: chipColors.secondary.backgroundColor,
            },
            backgroundColor: chipColors.secondary.backgroundColor,
            color: chipColors.secondary.color,
          },
          colorWarning: {
            '&:hover': {
              backgroundColor: chipColors.warning.backgroundColor,
            },
            backgroundColor: chipColors.warning.backgroundColor,
            color: chipColors.warning.color,
          },
          root: {
            ...omit(theme.typography.h6, ['color']),
            '&:hover': {
              backgroundColor: chipColors.default.backgroundColor,
            },
            backgroundColor: chipColors.default.backgroundColor,
            borderRadius: '12px',
            boxSizing: 'border-box',
            color: chipColors.default.color,
            display: 'inline-flex',
            height: 'unset',
            minHeight: '24px',
            minWidth: '64px',
          },
        },
      },

      // circular progress
      MuiCircularProgress: {
        styleOverrides: {
          determinate: {
            color: progressColor,
          },
        },
      },

      // baseline
      MuiCssBaseline: {
        styleOverrides: {
          // show learnosity dialog container above mui dialogs
          '.cke_dialog_container': {
            zIndex: `${theme.zIndex.modal} !important`,
          },
          // show the first dialog exclusively over the others
          '.MuiDialog-root ~ .MuiDialog-root': {
            display: 'none',
          },
        },
      },

      // date pickers
      MuiDatePicker: {
        defaultProps: {
          allowSameDateSelection: true,
          desktopModeMediaQuery,
        },
      },
      MuiDateRangePickerDay: {
        styleOverrides: {
          root: {
            '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
              '&.MuiDateRangePickerDay-outsideCurrentMonth.MuiDateRangePickerDay-hiddenDayFiller':
                {
                  background: 'transparent',
                },
              background: primaryColors.hover,
            },
          },
        },
      },
      MuiDateTimePicker: {
        defaultProps: {
          allowSameDateSelection: true,
          desktopModeMediaQuery,
        },
      },

      // dialog
      MuiDialog: {
        defaultProps: {
          maxWidth: 'xs',
        },
        styleOverrides: {
          paper: {
            '&.MuiDialog-paperScrollBody': {
              [theme.breakpoints.down(theme.breakpoints.values.md)]: {
                maxWidth: 'unset',
              },
            },
            backgroundImage: 'unset',
            margin: theme.spacing(1),
          },
          paperFullWidth: {
            width: 'calc(100% - 16px)',
          },
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: alpha(porticoThemeColors.black, 0.7),
            },
            '& .MuiDialog-paperWidthMd': {
              maxWidth: '960px',
            },
            '& .MuiDialog-paperWidthSm': {
              maxWidth: '784px',
            },
            '& .MuiDialog-paperWidthXs': {
              maxWidth: '640px',
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: `${theme.spacing(2.5)} ${theme.spacing(9)}`,
            [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
              padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            },
          },
          spacing: {
            '& > :not(:first-of-type)': {
              marginLeft: '16px',
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          dividers: {
            padding: `${theme.spacing(6)} ${theme.spacing(9)}`,
            [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
              padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            },
          },
          root: {
            padding: `${theme.spacing(6)} ${theme.spacing(9)}`,
            [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
              padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            lineHeight: 1,
            padding: `${theme.spacing(3)} ${theme.spacing(9)}`,
            [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
              padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            },
          },
        },
      },

      // dividers
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: lineColor,
            borderRightWidth: 'thin',
          },
        },
        variants: [
          {
            props: { variant: 'small' },
            style: {
              borderWidth: '1px',
            },
          },
        ],
      },

      // drawer
      MuiDrawer: {
        styleOverrides: {
          docked: {
            boxShadow: drawerShadow,
            zIndex: theme.zIndex.appBar - 1,
          },
        },
      },

      // fabs
      MuiFab: {
        styleOverrides: {
          primary: {
            backgroundColor: primaryColors.main,
            color: textColors.contrast,
          },
          root: {
            backgroundColor: porticoThemeColors.white,
          },
        },
      },

      // form
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            flex: 1,
            paddingBottom: '8px',
            paddingTop: '8px',
          },
          labelPlacementStart: {
            marginLeft: 0,
            width: '100%',
          },
          root: {
            alignItems: 'flex-start',
            marginLeft: '-8px',
            marginRight: 0,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              color: textColors.secondaryDisabled,
            },
            '&.Mui-error': {
              '&.Mui-disabled': {
                color: errorColors.disabled,
              },
              color: errorColors.main,
            },
            '&.Mui-focused': {
              '&.Mui-error': {
                color: errorColors.main,
              },
              color: primaryColors.main,
            },
            color: textColors.secondary,
            marginLeft: '16px',
          },
        },
      },

      // icon button
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: iconColor,
          },
          sizeSmall: {
            marginBottom: -4,
            marginTop: -4,
            padding: '4px',
            verticalAlign: 'text-bottom',
          },
        },
      },

      // input
      MuiInputBase: {
        variants: [
          {
            props: { size: 'large' },
            style: {
              '& .MuiOutlinedInput-input': {
                padding: '14px 16px',
              },
              '&.MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                minHeight: '48px',
                padding: '9px 16px',
              },
            },
          },
        ],
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            ...omit(theme.typography.subtitle2, ['color']),
            '&.Mui-disabled': {
              color: textColors.primaryDisabled,
            },
            '&.Mui-error': {
              color: errorColors.main,
            },
            color: textColors.primary,
            paddingBottom: '4px',
            position: 'relative',
            textAlign: 'left',
            transform: 'unset',
          },
        },
      },

      // linear progress
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: progressColor,
            borderRadius: '8px',
            height: '16px',
          },
        },
      },

      // links
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },

      // lists
      MuiList: {
        variants: [
          {
            // @ts-ignore: Revisit logic for setting custom variant. Could create a new common component.
            props: { variant: 'nav' },
            style: {
              display: 'grid',
              gap: '4px',
              padding: 0,
            },
          },
        ],
      },
      MuiListItem: {
        styleOverrides: {
          divider: {
            '&:last-child': {
              borderBottom: 'unset',
            },
            borderColor: lineColor,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              filter: 'grayscale(100%)',
            },
            '&.Mui-selected': {
              '&:hover': {
                backgroundColor: primaryColors.selected,
              },
              backgroundColor: primaryColors.selected,
            },
            color: textColors.primary,
            fontSize: '14px',
          },
        },
        variants: [
          {
            props: { variant: 'nav' },
            style: {
              '&.Mui-selected': {
                backgroundColor: primaryColors.selected,
                color: primaryColors.main,
                fontWeight: 'bold',
              },
              borderRadius: 6,
              minHeight: 40,
            },
          },
        ],
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: iconColor,
            height: '20px',
            marginLeft: '-2px',
            minWidth: '34px',
            position: 'relative',
            top: '-2px',
          },
        },
      },
      MuiListItemText: {
        defaultProps: {
          primaryTypographyProps: {
            variant: 'subtitle1',
          },
          secondaryTypographyProps: {
            paddingTop: 0.5,
            variant: 'caption',
          },
        },
        styleOverrides: {
          inset: {
            paddingLeft: '36px',
          },
          root: {
            margin: 0,
          },
        },
      },

      // menu
      MuiMenu: {
        defaultProps: {
          variant: 'menu',
        },
        styleOverrides: {
          list: {
            display: 'grid',
            gap: '4px',
            maxHeight: '272px',
            padding: '12px 8px',
          },
          root: {
            '& hr': {
              margin: '0 -8px',
            },
          },
        },
      },
      MuiMenuItem: {
        defaultProps: {
          disableTouchRipple: true,
        },
        styleOverrides: {
          root: {
            ...omit(theme.typography.subtitle2, ['color']),
            '&.Mui-disabled': {
              backgroundColor: 'unset',
            },
            '&.Mui-focusVisible': {
              backgroundColor: overlayColors.hover,
            },
            '&.Mui-selected': {
              '&.Mui-focusVisible': {
                backgroundColor: primaryColors.selected,
              },
              '&:hover': {
                backgroundColor: primaryColors.selected,
              },
              backgroundColor: primaryColors.selected,
            },
            '&:hover': {
              backgroundColor: overlayColors.hover,
            },
            borderRadius: '8px',
            color: textColors.primary,
            padding: '8px 16px',
          },
        },
      },

      // outlined input
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            ...omit(theme.typography.subtitle1, ['color']),
            '&.Mui-disabled': {
              color: outlinedInputColors.input.disabled.color,
            },
            '&.MuiInputBase-inputAdornedEnd': {
              paddingRight: 0,
            },
            '&.MuiSelect-select': {
              minHeight: 'auto',
            },
            '&::placeholder': {
              color: outlinedInputColors.input.placeholder.color,
              opacity: 1,
            },
            color: outlinedInputColors.input.color,
            height: 'auto',
            padding: '10px 16px',
          },
          root: {
            // outline color
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: outlinedInputColors.root.borderColor,
            },
            // icons
            '& .MuiSvgIcon-root': {
              color: outlinedInputColors.root.iconColor,
            },
            '& fieldset legend span:not(.notranslate)': {
              position: 'absolute',
            },
            // disabled input
            '&.Mui-disabled': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: outlinedInputColors.root.disabled.borderColor,
              },
              '& .MuiSvgIcon-root': {
                color: outlinedInputColors.root.disabled.iconColor,
              },
              color: outlinedInputColors.root.disabled.color,
            },
            // errored input
            '&.Mui-error:not(.Mui-disabled)': {
              // focused error input
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: outlinedInputColors.root.error.borderColor,
                borderWidth: '1px',
              },
              caretColor: outlinedInputColors.root.error.caretColor,
            },
            // focused input
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: outlinedInputColors.root.focused.borderColor,
                borderWidth: '1px',
              },
              caretColor: outlinedInputColors.root.focused.caretColor,
            },
            // adornments
            '&.MuiInputBase-adornedEnd': {
              paddingRight: '12px',
            },
            '&.MuiInputBase-multiline': {
              padding: 0,
            },
            // hovered input
            '&:hover': {
              '&:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: outlinedInputColors.root.hover.borderColor,
                },
            },
            backgroundColor: outlinedInputColors.root.backgroundColor,
            borderRadius: '8px',
            color: outlinedInputColors.root.color,
            height: 'auto',
          },
          sizeSmall: {
            '& .MuiOutlinedInput-input': {
              padding: '6px 16px',
            },
          },
        },
      },

      // paper
      MuiPaper: {
        styleOverrides: {
          elevation0: {
            boxShadow: 'none',
          },
          outlined: {
            border: `${lineWidth} solid ${lineColor}`,
            boxShadow: 'none',
            cursor: 'default',
          },
          root: {
            backgroundImage: 'unset',
            boxShadow: cardShadow,
            textDecoration: 'unset',
          },
          rounded: {
            borderRadius: '10px',
          },
        },
        variants: [
          {
            props: { variant: 'drag' },
            style: cardHoverStyles,
          },
          {
            props: { role: 'button', variant: 'elevation' },
            style: {
              '&:hover': cardHoverStyles,
              cursor: 'pointer',
              position: 'relative',
            },
          },
          {
            props: { role: 'tooltip', variant: 'elevation' },
            style: cardHoverStyles,
          },
        ],
      },

      // pickers
      MuiPickersDay: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              color: textColors.primaryDisabled,
            },
            '&.MuiDateRangePickerDay-dayInsideRangeInterval': {
              '&:hover': {
                background: primaryColors.hover,
              },
              color: textColors.primary,
            },
            '&.MuiPickersDay-today': {
              borderColor: primaryColors.main,
            },
            '&:hover': {
              background: primaryColors.hover,
              borderColor: 'transparent!important',
            },
            color: textColors.primary,
            fontWeight: 500,
          },
        },
      },

      // Date and time picker toolbar text (times and AM/PM buttons)
      MuiPickersToolbarText: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              color: primaryColors.main,
            },
          },
        },
      },

      // popover
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: cardHoverStyles.boxShadow,
          },
        },
      },

      // radios
      MuiRadio: {
        defaultProps: {
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              '&.Mui-checked': {
                color: primaryColors.disabled,
              },
              color: buttonColors.outlined.default.disabled,
            },
            '&.Mui-focusVisible': {
              '&.Mui-checked': {
                backgroundColor: primaryColors.hover,
              },
              backgroundColor: buttonColors.outlined.default.hover,
            },
            '&:hover': {
              '&.Mui-checked': {
                backgroundColor: primaryColors.hover,
              },
              backgroundColor: buttonColors.outlined.default.hover,
            },
            color: buttonColors.outlined.default.main,
            padding: '8px',
          },
        },
      },

      // select
      MuiSelect: {
        styleOverrides: {
          select: {
            minWidth: '80px',
          },
        },
      },

      MuiSkeleton: {
        defaultProps: {
          variant: 'rounded',
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },

      // sliders
      MuiSlider: {
        styleOverrides: {
          mark: {
            display: 'none',
          },
          rail: {
            backgroundColor: backgroundColors.default,
            borderRadius: 4,
            height: 8,
            opacity: 1,
          },
          root: {
            height: 8,
          },
          thumb: {
            '&:focus, &:hover, &.Mui-active': {
              boxShadow: 'inherit',
            },
            backgroundColor: porticoThemeColors.white,
            border: '2px solid currentColor',
            height: 24,
            width: 24,
          },
          track: {
            borderRadius: 4,
            height: 8,
          },
          valueLabel: {
            left: 'calc(-50% + 4px)',
          },
        },
      },

      // stack
      MuiStack: {
        defaultProps: {
          spacing: 3,
        },
      },

      // steppers
      MuiStepConnector: {
        styleOverrides: {
          line: {
            borderColor: lineColor,
            borderWidth: '2px',
          },
        },
      },
      MuiStepContent: {
        styleOverrides: {
          root: {
            borderColor: lineColor,
            borderWidth: '2px',
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: iconColor,
          },
          text: {
            fill: textColors.contrast,
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          horizontal: {
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${lineColor}`,
          },
          root: {
            padding: '24px',
          },
        },
      },

      // svg icon
      MuiSvgIcon: {
        styleOverrides: {
          colorAction: {
            color: iconColor,
          },
          fontSizeSmall: {
            fontSize: '16px',
          },
        },
      },

      // switches
      MuiSwitch: {
        styleOverrides: {
          root: {
            marginBottom: '-7px',
            marginTop: '-7px',
          },
          switchBase: {
            '&.Mui-checked': {
              // checked track
              '&+.MuiSwitch-track': {
                backgroundColor: switchColors.checked.track.backgroundColor,
                opacity: switchColors.checked.track.opacity,
              },
              // checked thumb
              color: switchColors.checked.thumb.color,
            },
            // unchecked thumb
            color: switchColors.unchecked.thumb.color,
          },
          thumb: {
            boxShadow: switchColors.common.thumb.boxShadow,
          },
          track: {
            backgroundColor: switchColors.unchecked.track.backgroundColor,
            opacity: switchColors.unchecked.track.opacity,
          },
        },
      },

      // tab
      MuiTab: {
        styleOverrides: {
          root: {
            '& .MuiTypography-root': {
              color: `${tabColors.textColor} !important`,
            },
            '&.Mui-selected .MuiTypography-root': {
              color: `${tabColors.selected.textColor} !important`,
            },
            fontWeight: 500,
            margin: '0 16px',
            minHeight: '56px',
            minWidth: 'unset',
            whiteSpace: 'pre',
          },
        },
      },

      // tables
      MuiTableCell: {
        styleOverrides: {
          head: {
            ...theme.typography.subtitle1,
            borderBottom: `1px solid ${lineColor}`,
            whiteSpace: 'pre',
          },
          root: {
            ...theme.typography.body2,
            '&.sticky-column': {
              boxShadow: drawerShadow,
            },
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${lineColor}`,
            whiteSpace: 'pre',
          },
          stickyHeader: {
            backgroundColor: theme.palette.background.paper,
            borderBottom: `${lineWidth} solid ${lineColor}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': {
              '&:hover': {
                cursor: 'pointer',
              },
            },
          },
        },
        variants: [
          {
            props: {
              // @ts-ignore: Some table rows can be dragged and thus would need custom styling.
              // We can set custom variants but Typescript will not understand it.
              variant: 'drag',
            },
            style: cardHoverStyles,
          },
        ],
      },

      // tabs
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: '6px',
          },
        },
      },

      // text fields
      MuiTextField: {
        defaultProps: {
          InputLabelProps: {
            required: false,
            shrink: true,
          },
          SelectProps: {
            displayEmpty: true,
          },
          size: 'medium',
          variant: 'outlined',
        },
      },

      // time picker
      MuiTimePicker: {
        defaultProps: {
          allowSameDateSelection: true,
          desktopModeMediaQuery,
        },
      },

      // toggles
      MuiToggleButton: {
        styleOverrides: {
          root: {
            ...omit(theme.typography.h6, ['color']),
            '&.Mui-selected': {
              '&:hover': {
                backgroundColor: toggleButtonColors.selected.backgroundColor,
              },
              backgroundColor: toggleButtonColors.selected.backgroundColor,
              color: toggleButtonColors.selected.color,
            },
            '&:hover': {
              backgroundColor: toggleButtonColors.hover.backgroundColor,
            },
            backgroundColor: toggleButtonColors.backgroundColor,
            border: `1px solid ${toggleButtonColors.borderColor}`,
            borderRadius: '6px',
            color: toggleButtonColors.color,
            padding: '9px 20px',
          },
          sizeSmall: {
            padding: '5px 12px',
          },
        },
      },
      MuiToggleButtonGroup: {
        defaultProps: {
          exclusive: true,
        },
        styleOverrides: {
          groupedHorizontal: {
            '&.Mui-selected': {
              '&:not(:first-of-type)': {
                border: `1px solid ${toggleButtonColors.selected.borderColor}`,
              },
              border: `1px solid ${toggleButtonColors.selected.borderColor}`,
              zIndex: 1,
            },
            '&:not(:first-of-type)': {
              border: `1px solid ${toggleButtonColors.borderColor}`,
            },
          },
        },
      },

      // typography
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: 'h1',
            h4: 'h2',
            h5: 'h3',
            h6: 'p',
            overline: 'h2',
            subtitle1: 'p',
            subtitle2: 'p',
          },
        },
        styleOverrides: {
          overline: {
            display: 'block',
            marginBottom: '20px',
          },
        },
      },

      // To use client-side checking only
      // https://mui.com/components/use-media-query/#client-side-only-rendering
      MuiUseMediaQuery: {
        defaultProps: {
          noSsr: true,
        },
      },
    },
  };

  return theme;
};

// Below are overrides for specifying custom sizes/variants.
declare module '@mui/material/styles' {
  interface Palette {
    alert: {
      contrastText: string;
      main: string;
    };
    boxShadow: string;
    brand: {
      blue: string;
    };
    buttonOutline: {
      color: string;
      hover: {
        backgroundColor: string;
      };
    };
    cardKnox: {
      input: {
        background: string;
        border: string;
        color: string;
      };
    };
    completed: Palette['primary'];
    current: Palette['primary'];
    declined: Palette['primary'];
    due: Palette['primary'];
    failed: Palette['primary'];
    fgBg: {
      error: {
        background: string;
        foreground: string;
      };
      info: {
        background: string;
        foreground: string;
      };
      neutral: {
        background: string;
        foreground: string;
      };
      primary: {
        background: string;
        foreground: string;
      };
      secondary: {
        background: string;
        foreground: string;
      };
      warning: {
        background: string;
        foreground: string;
      };
    };
    fill: {
      error: {
        backgroundColor: string;
        color: string;
      };
      neutral: {
        backgroundColor: string;
        color: string;
      };
      primary: {
        backgroundColor: string;
        color: string;
      };
      secondary: {
        backgroundColor: string;
        color: string;
      };
      warning: {
        backgroundColor: string;
        color: string;
      };
    };
    iconColor: string;
    learnosity: {
      error: {
        background: string;
        foreground: string;
      };
      success: {
        background: string;
        foreground: string;
      };
      warning: {
        background: string;
        foreground: string;
      };
    };
    lineColor: string;
    linkEmphasis: string;
    loa: Palette['primary'];
    login: {
      backgroundColor: string;
    };
    monochrome: string;
    nudge: {
      audiencePreview: {
        chip: {
          backgroundColor: string;
          color: string;
        };
      };
      channel: {
        backgroundColor: string;
        borderColor: string;
        iconColor: string;
      };
      dashboard: {
        categoryRate: {
          description: string;
          negative: string;
          positive: string;
        };
        channelDistribution: {
          neutral: string;
        };
        channelStats: {
          negative: string;
          neutral: string;
          positive: string;
        };
        charts: {
          blue: string;
          golden: string;
          neutral: string;
          pink: string;
          red: string;
          teal: string;
          vibrantTerracotta: string;
        };
        dateRange1: {
          backgroundColor: string;
          circle: string;
          color: string;
          description: string;
        };
        dateRange2: {
          backgroundColor: string;
          circle: string;
          color: string;
          description: string;
        };
        filters: {
          icon: string;
        };
        performanceComparison: {
          backgroundColor: string;
          color: string;
          title: string;
        };
        tables: {
          backgroundColor: string;
          color: string;
        };
        weekOverWeek: {
          active: {
            background: string;
            border: string;
            percentage: string;
            text: string;
            title: string;
            total: string;
          };
          inactive: {
            background: string;
            border: string;
            percentage: string;
            text: string;
            title: string;
            total: string;
          };
        };
      };
      filter: {
        backgroundColor: string;
        borderColor: string;
        iconColor: string;
      };
      frequency: {
        backgroundColor: string;
        borderColor: string;
        iconColor: string;
      };
    };
    outstanding: Palette['primary'];
    overdue: Palette['primary'];
    overlay: {
      hover: string;
      selected: string;
    };
    paid: Palette['primary'];
    partial: Palette['primary'];
    pastdue: Palette['primary'];
    planCard: {
      button: {
        backgroundColor: string;
        hoverBackgroundColor: string;
      };
    };
    refunded: Palette['primary'];
    separator: string;
    surface: {
      card: {
        onHover: string;
      };
      chart: {
        backgroundColor: string;
        primary: string;
      };
      chip: {
        selectedBg: string;
        selectedColor: string;
      };
    };
    tableHeader: {
      accentBackground: string;
      infoBackground: string;
    };
    tip: {
      main: string;
    };
    toggle: {
      backgroundColor: string;
      borderColor: string;
      color: string;
      hover: {
        backgroundColor: string;
      };
      selected: {
        backgroundColor: string;
        borderColor: string;
        color: string;
      };
    };
    upcoming: Palette['primary'];
    void: Palette['primary'];
  }
  interface PaletteOptions {
    alert: {
      contrastText: string;
      main: string;
    };
    boxShadow: string;
    brand: {
      blue: string;
    };
    buttonOutline: {
      color: string;
      hover: {
        backgroundColor: string;
      };
    };
    cardKnox: {
      input: {
        background: string;
        border: string;
        color: string;
      };
    };
    completed?: PaletteOptions['primary'];
    current?: PaletteOptions['primary'];
    declined?: PaletteOptions['primary'];
    due?: PaletteOptions['primary'];
    failed?: PaletteOptions['primary'];
    fgBg: {
      error: {
        background: string;
        foreground: string;
      };
      info: {
        background: string;
        foreground: string;
      };
      neutral: {
        background: string;
        foreground: string;
      };
      primary: {
        background: string;
        foreground: string;
      };
      secondary: {
        background: string;
        foreground: string;
      };
      warning: {
        background: string;
        foreground: string;
      };
    };
    fill: {
      error: {
        backgroundColor: string;
        color: string;
      };
      neutral: {
        backgroundColor: string;
        color: string;
      };
      primary: {
        backgroundColor: string;
        color: string;
      };
      secondary: {
        backgroundColor: string;
        color: string;
      };
      warning: {
        backgroundColor: string;
        color: string;
      };
    };
    iconColor: string;
    learnosity: {
      error: {
        background: string;
        foreground: string;
      };
      success: {
        background: string;
        foreground: string;
      };
      warning: {
        background: string;
        foreground: string;
      };
    };
    lineColor: string;
    linkEmphasis: string;
    loa?: PaletteOptions['primary'];
    login: {
      backgroundColor: string;
    };
    monochrome: string;
    nudge: {
      audiencePreview: {
        chip: {
          backgroundColor: string;
          color: string;
        };
      };
      channel: {
        backgroundColor: string;
        borderColor: string;
        iconColor: string;
      };
      dashboard: {
        categoryRate: {
          description: string;
          negative: string;
          positive: string;
        };
        channelDistribution: {
          neutral: string;
        };
        channelStats: {
          negative: string;
          neutral: string;
          positive: string;
        };
        charts: {
          blue: string;
          golden: string;
          neutral: string;
          pink: string;
          red: string;
          teal: string;
          vibrantTerracotta: string;
        };
        dateRange1: {
          backgroundColor: string;
          circle: string;
          color: string;
          description: string;
        };
        dateRange2: {
          backgroundColor: string;
          circle: string;
          color: string;
          description: string;
        };
        filters: {
          icon: string;
        };
        performanceComparison: {
          backgroundColor: string;
          color: string;
          title: string;
        };
        tables: {
          backgroundColor: string;
          color: string;
        };
        weekOverWeek: {
          active: {
            background: string;
            border: string;
            percentage: string;
            text: string;
            title: string;
            total: string;
          };
          inactive: {
            background: string;
            border: string;
            percentage: string;
            text: string;
            title: string;
            total: string;
          };
        };
      };
      filter: {
        backgroundColor: string;
        borderColor: string;
        iconColor: string;
      };
      frequency: {
        backgroundColor: string;
        borderColor: string;
        iconColor: string;
      };
    };
    outstanding?: PaletteOptions['primary'];
    overdue?: PaletteOptions['primary'];
    overlay: {
      hover: string;
      selected: string;
    };
    paid?: PaletteOptions['primary'];
    partial?: PaletteOptions['primary'];
    pastdue?: PaletteOptions['primary'];
    planCard: {
      button: {
        backgroundColor: string;
        hoverBackgroundColor: string;
      };
    };
    refunded?: PaletteOptions['primary'];
    separator: string;
    surface: {
      card: {
        onHover: string;
      };
      chart: {
        backgroundColor: string;
        primary: string;
      };
      chip: {
        selectedBg: string;
        selectedColor: string;
      };
    };
    tableHeader: {
      accentBackground: string;
      infoBackground: string;
    };
    tip: {
      main: string;
    };
    toggle: {
      backgroundColor: string;
      borderColor: string;
      color: string;
      hover: {
        backgroundColor: string;
      };
      selected: {
        backgroundColor: string;
        borderColor: string;
        color: string;
      };
    };
    upcoming?: PaletteOptions['primary'];
    void: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Chip' {
  // Our design system does not allow for a small size chip.
  // Will throw Typescript warning if specifying size="small".
  interface ChipPropsSizeOverrides {
    small?: false;
  }

  interface ChipPropsColorOverrides {
    completed: true;
    current: true;
    declined: true;
    due: true;
    failed: true;
    loa: true;
    outstanding: true;
    overdue: true;
    paid: true;
    partial: true;
    pastdue: true;
    refunded: true;
    upcoming: true;
    void: true;
  }

  interface CustomChipStyleOverrides extends Partial<
    OverridesStyleRules<keyof ChipClasses, 'MuiChip', typeof useTheme>
  > {
    colorOutstanding: {
      '&:hover': {
        backgroundColor: string;
      };
      backgroundColor: string;
      color: string;
    };
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    drag: true;
  }
}

declare module '@mui/material/InputBase' {
  interface InputBasePropsSizeOverrides {
    large: true;
  }
}

declare module '@mui/material/ListItemButton' {
  interface ListItemButtonBaseProps {
    variant?: 'nav';
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    large: true;
  }
}

declare module '@mui/material/Divider' {
  interface DividerPropsVariantOverrides {
    small: true;
  }
}
